"use client";

import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { getAllBooks } from "../../utils/apiSample/book-data";
import {
  Cache_Tag_Key,
  getCacheTags,
  getPreferredTags,
} from "../../utils/apiSample/recommendation";
import { PreferencesButton } from "../sharedcomponents/preferenceManager";
import { IBookTagData } from "../../apiservice/userService.type";
import { userGetBookTags } from "../../apiservice/userService";
import { Spin } from "antd";
import { BookSliderSkeleton } from "../BookComponents/BookSkeletonLoading";

export const HomeTagsSlider = () => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [tags, setTags] = useState<IBookTagData[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load tags based on preferences
  // const loadTags = () => {
  //   // Extract all unique tags from books
  //   const allBooks = getAllBooks();
  //   const allTags = new Set<string>();

  //   allBooks.forEach((book) => {
  //     if (book.tags && book.tags.length > 0) {
  //       book.tags.forEach((tag) => allTags.add(tag));
  //     }
  //   });

  //   // Get preferred tags
  //   const preferredTags = getPreferredTags();

  //   // Sort tags: preferred first, then alphabetically
  //   const sortedTags = Array.from(allTags).sort((a, b) => {
  //     const aPreferred = preferredTags.includes(a);
  //     const bPreferred = preferredTags.includes(b);

  //     if (aPreferred && !bPreferred) return -1;
  //     if (!aPreferred && bPreferred) return 1;
  //     return a.localeCompare(b);
  //   });

  //   setTags(sortedTags);
  // };

  // Load tags on mount and when preferences change
  useEffect(() => {
    loadTags();
  }, []);

  // Check if we need to show navigation arrows
  useEffect(() => {
    const checkScroll = () => {
      if (!sliderRef.current) return;

      setShowLeftArrow(sliderRef.current.scrollLeft > 0);
      setShowRightArrow(
        sliderRef.current.scrollLeft <
          sliderRef.current.scrollWidth - sliderRef.current.clientWidth - 10
      );
    };

    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener("scroll", checkScroll);
      // Initial check
      checkScroll();
    }

    return () => {
      if (slider) {
        slider.removeEventListener("scroll", checkScroll);
      }
    };
  }, [tags]);

  // Scroll functions
  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const loadTags = async () => {
    let allTags: IBookTagData[] = [];
    try {
      const cacheData = await getCacheTags();
      allTags = cacheData;
      setTags(cacheData);
      const tagData = await userGetBookTags();
      if (tagData) {
        setTags(tagData?.data);
        allTags = tagData?.data;
        localStorage.setItem(Cache_Tag_Key, JSON.stringify(tagData?.data));
      } else {
        setError("Tag not found");
      }
    } catch (err) {
      setError("Failed to load Tag data");
    } finally {
      setLoading(false);
    }

    const preferredTags = getPreferredTags();

    // If there are preferred Tags, prioritize them
    // Sort tags: preferred first, then alphabetically
    const sortedTags = Array.from(allTags).sort((a, b) => {
      const aPreferred = preferredTags.includes(a.title);
      const bPreferred = preferredTags.includes(b.title);

      if (aPreferred && !bPreferred) return -1;
      if (!aPreferred && bPreferred) return 1;
      return a.title.localeCompare(b.title);
    });

    setTags(sortedTags);
  };

  useEffect(() => {
    loadTags();
  }, []);

  if (loading) {
    return (
    <div className="container mx-auto p-4">
        <BookSliderSkeleton title="Popular Tags" height="12" />
      </div>
    );
  }

  if (error || !tags) {
    return (
      <div className="p-4 text-center text-red-500">
        {error || "Tag not found"}
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg tracking-tight text-gray-900 dark:text-stone-100">
          Popular Tags
        </h3>
        <div className="flex items-center gap-2">
          <PreferencesButton
            onPreferencesUpdated={loadTags}
            activeIndex="tags"
            buttonText="Edit Tags"
            modalTitle="Select Your Tags"
          />

          {showLeftArrow && (
            <button
              onClick={scrollLeft}
              className="w-8 h-8 flex items-center justify-center text-xs bg-gray-100 dark:bg-stone-700 rounded-full hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors text-gray-700 dark:text-stone-200"
              aria-label="Scroll left"
            >
              <LeftOutlined />
            </button>
          )}
          {showRightArrow && (
            <button
              onClick={scrollRight}
              className="w-8 h-8 flex items-center justify-center text-xs bg-gray-100 dark:bg-stone-700 rounded-full hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors text-gray-700 dark:text-stone-200"
              aria-label="Scroll right"
            >
              <RightOutlined />
            </button>
          )}
        </div>
      </div>

      <div
        ref={sliderRef}
        className="flex overflow-x-auto pb-2 gap-2 scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {tags.map((tag, index) => {
          const isPreferred = getPreferredTags().includes(tag.title);

          return (
            <Link
              key={index}
              to={`/users/tag-search?tag=${tag.title}`}
              className="flex-shrink-0"
            >
              <div
                className={`flex items-center px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  isPreferred
                    ? "bg-indigo-100 text-indigo-800 font-medium dark:bg-indigo-900 dark:text-indigo-200"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-stone-700 dark:text-stone-200 dark:hover:bg-stone-600"
                }`}
              >
                <span className="opacity-60 mr-0.5">#</span>
                <span className="capitalize">{tag.title}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default HomeTagsSlider;
